import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';  // 使用正确配置的axios实例
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faClock, faTrash, faEye, faHeart, faDumbbell, faUser, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import '../../styles/UserCenter.css';

// 本地内联占位图，避免外部域名依赖
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="150"><rect width="100%" height="100%" fill="#eeeeee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999999" font-size="16">No Image</text></svg>';

// 模拟收藏数据
const mockFavorites = [
  {
    id: 1,
    userId: 1647488892731,
    type: 'coach',
    objectId: 101,
    objectName: '张教练',
    objectImage: PLACEHOLDER_IMG,
    createTime: '2023-05-15T08:30:00'
  },
  {
    id: 2,
    userId: 1647488892731,
    type: 'project',
    objectId: 201,
    objectName: '力量训练',
    objectImage: PLACEHOLDER_IMG,
    createTime: '2023-06-20T14:45:00'
  },
  {
    id: 3,
    userId: 1647488892731,
    type: 'course',
    objectId: 301,
    objectName: '瑜伽初级班',
    objectImage: PLACEHOLDER_IMG,
    createTime: '2023-07-10T10:15:00'
  }
];

const Favorites = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFavorite, setSelectedFavorite] = useState(null);

  useEffect(() => {
    // 从localStorage获取用户信息
    let storedUser = localStorage.getItem('userData');
    
    // 如果userData不存在，尝试使用user键
    if (!storedUser) {
      storedUser = localStorage.getItem('user');
      // 如果找到了user信息，同时存储到userData中以保持一致性
      if (storedUser) {
        localStorage.setItem('userData', storedUser);
      }
    }
    
    if (!storedUser) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      fetchFavorites(parsedUser.id);
    } catch (err) {
      console.error('Error parsing user data', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchFavorites = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      // 首先尝试从后端获取数据
      try {
        const response = await axios.get(`/favorite/user/${userId}`);
        
        if (response.data.code === 0) {
          setFavorites(response.data.data || []);
          setLoading(false);
          return;
        }
      } catch (apiError) {
        console.log('API请求失败，使用模拟数据', apiError);
        // API请求失败时，不立即设置错误，继续使用模拟数据
      }
      
      // 如果API请求失败，使用模拟数据
      console.log('使用模拟收藏数据');
      setTimeout(() => {
        setFavorites(mockFavorites);
        setLoading(false);
      }, 500); // 添加短暂延迟模拟加载过程
      
    } catch (err) {
      console.error('Error fetching favorites', err);
      setError('获取收藏列表失败，请稍后再试');
      setLoading(false);
    }
  };

  const filterFavorites = () => {
    if (activeTab === 'all') {
      return favorites;
    }
    return favorites.filter(favorite => favorite.type === activeTab);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleRemove = (favorite) => {
    setSelectedFavorite(favorite);
    setShowConfirmModal(true);
  };

  const confirmRemove = async () => {
    if (!selectedFavorite) return;
    
    setLoading(true);
    
    // 在前端直接移除，无需等待API响应
    setFavorites(favorites.filter(f => f.id !== selectedFavorite.id));
    setShowConfirmModal(false);
    setSelectedFavorite(null);
    setLoading(false);
    
    // 尝试调用API但不依赖其结果
    try {
      await axios.delete(`/favorite/delete/${selectedFavorite.id}`);
      // 成功删除，但我们已经更新了UI，所以这里不需要额外处理
    } catch (err) {
      console.error('无法从服务器删除收藏，但已在前端移除', err);
      // 不向用户显示错误，因为从用户角度来看操作已成功
    }
  };

  const handleView = (favorite) => {
    switch (favorite.type) {
      case 'coach':
        navigate(`/technician/${favorite.objectName}`);
        break;
      case 'project':
        navigate(`/service/${favorite.objectId}`);
        break;
      case 'course':
        navigate(`/course/${favorite.objectId}`);
        break;
      default:
        break;
    }
  };

  const getFavoriteTypeIcon = (type) => {
    switch (type) {
      case 'coach':
        return <FontAwesomeIcon icon={faUser} />;
      case 'project':
        return <FontAwesomeIcon icon={faDumbbell} />;
      case 'course':
        return <FontAwesomeIcon icon={faGraduationCap} />;
      default:
        return <FontAwesomeIcon icon={faHeart} />;
    }
  };

  const getFavoriteTypeName = (type) => {
    switch (type) {
      case 'coach':
        return '教练';
      case 'project':
        return '健身项目';
      case 'course':
        return '课程';
      default:
        return '收藏';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="user-center-container">
      <div className="page-header">
        <button className="btn-link" onClick={() => {
          if (userData && userData.id) {
            navigate(`/users/${userData.id}`);
          } else {
            navigate('/user-center');
          }
        }}>
          <FontAwesomeIcon icon={faArrowLeft} /> 返回个人中心
        </button>
        <h1>我的收藏</h1>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faTrash} /> {error}
        </div>
      )}

      <div className="favorites-container">
        <div className="favorites-tabs">
          <div 
            className={`favorites-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            全部收藏
          </div>
          <div 
            className={`favorites-tab ${activeTab === 'coach' ? 'active' : ''}`}
            onClick={() => handleTabChange('coach')}
          >
            教练收藏
          </div>
          <div 
            className={`favorites-tab ${activeTab === 'project' ? 'active' : ''}`}
            onClick={() => handleTabChange('project')}
          >
            健身项目收藏
          </div>
          <div 
            className={`favorites-tab ${activeTab === 'course' ? 'active' : ''}`}
            onClick={() => handleTabChange('course')}
          >
            课程收藏
          </div>
        </div>

        {loading ? (
          <div className="loading-message">加载中...</div>
        ) : filterFavorites().length === 0 ? (
          <div className="no-data-message">
            {activeTab === 'all' ? '暂无收藏内容' : `暂无${getFavoriteTypeName(activeTab)}收藏`}
          </div>
        ) : (
          <div className="favorites-grid">
            {filterFavorites().map(favorite => (
              <div className="favorite-item" key={favorite.id}>
                <div className="favorite-image">
                  <img 
                    src={favorite.objectImage || PLACEHOLDER_IMG}
                    alt={favorite.objectName}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = PLACEHOLDER_IMG;
                    }}
                  />
                  <span className="favorite-type">
                    {getFavoriteTypeIcon(favorite.type)} {getFavoriteTypeName(favorite.type)}
                  </span>
                </div>
                <div className="favorite-content">
                  <h3>{favorite.objectName}</h3>
                  <div className="favorite-time">
                    <FontAwesomeIcon icon={faClock} />
                    <span>收藏于 {formatDate(favorite.createTime)}</span>
                  </div>
                  <div className="favorite-actions">
                    <button 
                      className="favorite-btn view-btn"
                      onClick={() => handleView(favorite)}
                    >
                      <FontAwesomeIcon icon={faEye} /> 查看详情
                    </button>
                    <button 
                      className="favorite-btn remove-btn"
                      onClick={() => handleRemove(favorite)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> 取消收藏
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showConfirmModal && (
        <div className="confirm-modal">
          <div className="confirm-modal-content">
            <div className="confirm-modal-header">
              <h3>确认取消收藏</h3>
              <button 
                className="confirm-modal-close"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedFavorite(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="confirm-modal-body">
              <p>您确定要取消收藏"{selectedFavorite?.objectName}"吗？此操作无法撤销。</p>
            </div>
            <div className="confirm-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedFavorite(null);
                }}
              >
                取消
              </button>
              <button 
                className="btn btn-danger"
                onClick={confirmRemove}
              >
                确认取消收藏
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Favorites; 